const FORMULA_PREFIX = /^[\s]*[=+\-@]/;

/**
 * Strip a leading UTF-8 BOM.
 *
 * Both browser read paths (`FileReader.readAsText`, `Blob.text()`) already
 * BOM-sniff per the Encoding Standard, so this is defence in depth — but it is
 * load-bearing for unit tests, which hand `parseCsvText` a raw string with no
 * decode step. If a BOM ever survives, `headers[0]` becomes `'\uFEFFsku'` and
 * every upload fails with "Missing required column: sku".
 */
export const stripBom = (text: string): string =>
  text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

/** Prepended to exported CSVs so Excel on Windows renders CJK instead of mojibake. */
export const UTF8_BOM = '\uFEFF';

const sanitizeCsvValue = (value: unknown): string => {
  // A finite JS number can never be a spreadsheet formula: String(n) is always
  // /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/, so it contains no CSV metacharacter and
  // can only ever LEAD with '-' followed by a digit — no formula body can
  // follow. Guarding it emitted `'-5`, which parseInt() read as NaN and
  // useCsvParser coerced to 0, silently ZEROING negative-stock SKUs on a
  // round trip. Negative stock is deliberate in this repo (it is the
  // discrepancy signal a stocktake exists to surface) — see
  // architectural-decisions.md "Negative Stock Levels".
  //
  // This is a runtime `typeof`, so a mistyped string cannot reach the branch.
  // Narrowing FORMULA_PREFIX instead was rejected: any content-based rule must
  // admit `-1`, which admits the live Excel DDE payload
  // `-1+1+cmd|' /C calc'!A0`. Only guard values that arrived as text.
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);

  const raw = value == null ? '' : String(value);
  const guarded = FORMULA_PREFIX.test(raw) ? `'${raw}` : raw;
  const escaped = guarded.replace(/"/g, '""');
  const shouldQuote = /[",\n\r]/.test(escaped);
  return shouldQuote ? `"${escaped}"` : escaped;
};

export interface ToCsvOptions {
  /** Row terminator. Default '\n'; pass '\r\n' for maximum Excel compatibility. */
  eol?: string;
}

const toCsvRow = (values: unknown[]): string =>
  values.map(sanitizeCsvValue).join(',');

export const toCsvContent = (rows: unknown[][], options?: ToCsvOptions): string =>
  rows.map(toCsvRow).join(options?.eol ?? '\n');

/**
 * Split a quote-aware CSV field list out of ONE record (no embedded newlines).
 *
 * Fixed defect: the escape branch used to fire without checking `inQuotes`, so
 * a quoted EMPTY field (`a,"",b` — exactly what Excel writes for a blank cell)
 * parsed to a literal `"` instead of `''`. A doubled quote only escapes when
 * we are already inside a quoted field.
 */
export const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
};

export interface SplitCsvRecordsResult {
  records: string[];
  /**
   * True when the text ended mid-quote. The caller should treat the file as
   * malformed rather than trusting the (necessarily guessed) record split.
   */
  unterminatedQuote: boolean;
}

/**
 * Split CSV text into RECORDS, honouring newlines inside quoted fields.
 *
 * `toCsvContent` legitimately quotes any value containing \n or \r, so a naive
 * `text.split(/\r?\n/)` desynchronises every subsequent row — i.e. our own
 * importer could not read our own export. Exporters should still flatten
 * newlines out of free-text columns; this makes the reader correct regardless.
 *
 * Blank records are dropped. On an unterminated quote we fall back to a plain
 * line split so the caller can still surface per-line errors, and flag it.
 */
export const splitCsvRecords = (text: string): SplitCsvRecordsResult => {
  const src = stripBom(text);
  const records: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const char = src[i];

    if (char === '"') {
      // Mirror parseCsvLine: a doubled quote only escapes inside a quoted field.
      if (inQuotes && src[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      // Consume CRLF as one terminator.
      if (char === '\r' && src[i + 1] === '\n') i++;
      if (current.trim()) records.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) records.push(current);

  if (inQuotes) {
    return {
      records: src.split(/\r?\n/).filter((line) => line.trim()),
      unterminatedQuote: true,
    };
  }

  return { records, unterminatedQuote: false };
};

export interface CsvRecordWithLine {
  text: string;
  /** 1-based physical line the record starts on (what a spreadsheet calls the row). */
  line: number;
}

export interface SplitCsvRecordsWithLinesResult {
  records: CsvRecordWithLine[];
  unterminatedQuote: boolean;
}

/**
 * `splitCsvRecords` plus two things an importer with a hand-edited template
 * needs: each record keeps the physical line it starts on (so "row 14" in an
 * error is row 14 in the spreadsheet, comment lines counted), and, with
 * `commentPrefix`, a line whose first non-space text is that prefix is skipped
 * when it STARTS a record. A prefix inside a quoted field is data, never a
 * comment, and a comment line's own quotes are never counted.
 *
 * Same record rules as `splitCsvRecords` (quote-aware, CRLF as one break,
 * blank records dropped). On an unterminated quote it falls back to a plain
 * line split, comments still skipped, and flags it.
 */
export const splitCsvRecordsWithLines = (
  text: string,
  options: { commentPrefix?: string } = {},
): SplitCsvRecordsWithLinesResult => {
  const src = stripBom(text);
  const { commentPrefix } = options;
  const isComment = (lineText: string) => !!commentPrefix && lineText.trimStart().startsWith(commentPrefix);
  const records: CsvRecordWithLine[] = [];
  let current = '';
  let currentLine = 1;
  let line = 1;
  let inQuotes = false;
  let atRecordStart = true;

  for (let i = 0; i < src.length; i++) {
    if (atRecordStart && commentPrefix) {
      // Peek the physical line: a comment is skipped whole, quotes and all.
      let end = i;
      while (end < src.length && src[end] !== '\n' && src[end] !== '\r') end++;
      if (isComment(src.slice(i, end))) {
        i = end;
        if (src[i] === '\r' && src[i + 1] === '\n') i++;
        line++;
        continue;
      }
    }
    if (atRecordStart) {
      currentLine = line;
      atRecordStart = false;
    }

    const char = src[i];
    if (char === '"') {
      if (inQuotes && src[i + 1] === '"') {
        current += '""';
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      current += char;
      continue;
    }

    if (char === '\n' || char === '\r') {
      if (char === '\r' && src[i + 1] === '\n') i++;
      line++;
      if (inQuotes) {
        current += '\n';
        continue;
      }
      if (current.trim()) records.push({ text: current, line: currentLine });
      current = '';
      atRecordStart = true;
      continue;
    }

    current += char;
  }

  if (current.trim()) records.push({ text: current, line: currentLine });

  if (inQuotes) {
    return {
      records: src
        .split(/\r?\n|\r/)
        .map((lineText, index) => ({ text: lineText, line: index + 1 }))
        .filter((record) => record.text.trim() && !isComment(record.text)),
      unterminatedQuote: true,
    };
  }

  return { records, unterminatedQuote: false };
};
